import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Save, RefreshCw, AlertCircle, Upload, Image, Calendar, Plus, Check, Archive, Trash2, Users } from 'lucide-react';
import {
  getSportsYears,
  createSportsYear,
  activateSportsYear,
  updateSportsYear,
  deleteSportsYear,
} from '../../services/sportsYearService';
import { snapshotEnrollmentsForYear } from '../../services/studentEnrollmentService';
import { SportsYear, SportsYearStatus } from '../../types/models';

interface SystemSettings {
  auto_reference_number: {
    enabled: boolean;
    next_number: number;
    prefix: string;
  };
  school_logo?: {
    url: string;
    updated_at: string;
  };
}

const SystemSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SystemSettings>({
    auto_reference_number: {
      enabled: false,
      next_number: 1000,
      prefix: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [sportsYears, setSportsYears] = useState<SportsYear[]>([]);
  const [yearsLoading, setYearsLoading] = useState(false);
  const [yearActionLoading, setYearActionLoading] = useState<string | null>(null);
  const [newYear, setNewYear] = useState<{
    year: string;
    label: string;
    start_date: string;
    end_date: string;
    status: SportsYearStatus;
  }>({
    year: new Date().getFullYear().toString(),
    label: '',
    start_date: '',
    end_date: '',
    status: 'planning',
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to access this page');
        setLoading(false);
        return;
      }

      // Check if user has admin role
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          roles!inner (
            role_name
          )
        `)
        .eq('user_id', user.id);

      if (rolesError) throw rolesError;

      const isUserAdmin = userRoles?.some(ur => ur.roles.role_name === 'admin') ?? false;
      setIsAdmin(isUserAdmin);

      if (isUserAdmin) {
        await Promise.all([fetchSettings(), fetchSportsYears()]);
      } else {
        setError('Access denied. Only administrators can access system settings.');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Error checking admin access:', error);
      setError('Failed to verify access permissions');
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch auto_reference_number setting
      const { data: refNumData, error: refNumError } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_name', 'auto_reference_number')
        .maybeSingle();
      
      if (refNumError) throw refNumError;

      // Fetch school_logo setting
      const { data: logoData, error: logoError } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_name', 'school_logo')
        .maybeSingle();
      
      if (logoError) throw logoError;

      const settingsObj: SystemSettings = {
        auto_reference_number: refNumData?.setting_value || {
          enabled: false,
          next_number: 1000,
          prefix: ''
        }
      };

      if (logoData?.setting_value) {
        settingsObj.school_logo = logoData.setting_value;
        if (logoData.setting_value.url) {
          setLogoPreview(logoData.setting_value.url);
        }
      }

      setSettings(settingsObj);
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      setError(error.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Check if auto_reference_number setting exists
      const { data: existingSettings, error: checkError } = await supabase
        .from('system_settings')
        .select('setting_id')
        .eq('setting_name', 'auto_reference_number')
        .maybeSingle();
      
      if (checkError) throw checkError;
      
      if (existingSettings) {
        // Update existing setting
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({ 
            setting_value: settings.auto_reference_number,
            updated_at: new Date()
          })
          .eq('setting_name', 'auto_reference_number');
        
        if (updateError) throw updateError;
      } else {
        // Insert new setting
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{ 
            setting_name: 'auto_reference_number',
            setting_value: settings.auto_reference_number
          }]);
        
        if (insertError) throw insertError;
      }
      
      setSuccess('Settings saved successfully');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setError(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateReferenceNumbers = async () => {
    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);
      
      // Call the RPC function to generate reference numbers
      const { data, error } = await supabase.rpc('generate_reference_numbers_for_existing_students');
      
      if (error) throw error;
      
      setSuccess(`Reference numbers generated successfully for all participants without reference numbers. ${data?.updated_count || 0} participants updated.`);
      
      // Refresh settings to get the updated next_number
      await fetchSettings();
    } catch (error: any) {
      console.error('Error generating reference numbers:', error);
      setError(error.message || 'Failed to generate reference numbers');
    } finally {
      setGenerating(false);
    }
  };

  const handleChange = (
    settingName: keyof SystemSettings, 
    field: string, 
    value: string | number | boolean
  ) => {
    setSettings(prev => ({
      ...prev,
      [settingName]: {
        ...prev[settingName],
        [field]: value
      }
    }));
  };

  const fetchSportsYears = async () => {
    try {
      setYearsLoading(true);
      const data = await getSportsYears();
      setSportsYears(data);
    } catch (err: any) {
      console.error('Error fetching sports years:', err);
      setError(err.message || 'Failed to load sports years');
    } finally {
      setYearsLoading(false);
    }
  };

  const handleCreateSportsYear = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const yearNum = parseInt(newYear.year, 10);
    if (!yearNum || yearNum < 1900 || yearNum > 2100) {
      setError('Year must be between 1900 and 2100');
      return;
    }

    try {
      setYearActionLoading('create');
      await createSportsYear({
        year: yearNum,
        label: newYear.label || null,
        start_date: newYear.start_date || null,
        end_date: newYear.end_date || null,
        status: newYear.status,
      });
      setSuccess(`Sports year ${yearNum} created`);
      setNewYear({
        year: (yearNum + 1).toString(),
        label: '',
        start_date: '',
        end_date: '',
        status: 'planning',
      });
      await fetchSportsYears();
    } catch (err: any) {
      console.error('Error creating sports year:', err);
      setError(err.message || 'Failed to create sports year');
    } finally {
      setYearActionLoading(null);
    }
  };

  const handleActivateYear = async (year: number) => {
    setError(null);
    setSuccess(null);
    try {
      setYearActionLoading(`activate-${year}`);
      await activateSportsYear(year);
      setSuccess(`Sports year ${year} is now active`);
      await fetchSportsYears();
    } catch (err: any) {
      console.error('Error activating sports year:', err);
      setError(err.message || 'Failed to activate sports year');
    } finally {
      setYearActionLoading(null);
    }
  };

  const handleArchiveYear = async (yearRow: SportsYear) => {
    setError(null);
    setSuccess(null);
    try {
      setYearActionLoading(`archive-${yearRow.year_id}`);
      await updateSportsYear(yearRow.year_id, { status: 'archived' });
      setSuccess(`Sports year ${yearRow.year} archived`);
      await fetchSportsYears();
    } catch (err: any) {
      console.error('Error archiving sports year:', err);
      setError(err.message || 'Failed to archive sports year');
    } finally {
      setYearActionLoading(null);
    }
  };

  const handleSnapshotEnrollments = async (yearRow: SportsYear) => {
    setError(null);
    setSuccess(null);
    try {
      setYearActionLoading(`snapshot-${yearRow.year_id}`);
      const inserted = await snapshotEnrollmentsForYear(yearRow.year);
      if (inserted > 0) {
        setSuccess(`Snapshotted ${inserted} student enrollments for ${yearRow.year}`);
      } else {
        setSuccess(`All students already have enrollments for ${yearRow.year}`);
      }
    } catch (err: any) {
      console.error('Error snapshotting enrollments:', err);
      setError(err.message || 'Failed to snapshot enrollments');
    } finally {
      setYearActionLoading(null);
    }
  };

  const handleDeleteYear = async (yearRow: SportsYear) => {
    if (yearRow.status === 'active') {
      setError('Cannot delete an active sports year. Activate another year first.');
      return;
    }
    if (!window.confirm(`Delete sports year ${yearRow.year}? This cannot be undone.`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      setYearActionLoading(`delete-${yearRow.year_id}`);
      await deleteSportsYear(yearRow.year_id);
      setSuccess(`Sports year ${yearRow.year} deleted`);
      await fetchSportsYears();
    } catch (err: any) {
      console.error('Error deleting sports year:', err);
      setError(err.message || 'Failed to delete sports year. It may be in use.');
    } finally {
      setYearActionLoading(null);
    }
  };

  const statusBadge = (status: SportsYearStatus) => {
    const styles: Record<SportsYearStatus, string> = {
      active: 'bg-green-100 text-green-800',
      planning: 'bg-blue-100 text-blue-800',
      archived: 'bg-gray-100 text-gray-700',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file type
      if (!file.type.match('image.*')) {
        setError('Please select an image file (JPEG, PNG, GIF)');
        return;
      }
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2MB');
        return;
      }
      
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    
    try {
      setUploadingLogo(true);
      setError(null);
      
      // Generate a unique file name
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `school-logo-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      // Upload the file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, logoFile, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) throw new Error(`Error uploading logo: ${uploadError.message}`);
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);
      
      // Save the logo URL in system_settings
      const logoSettings = {
        url: publicUrl,
        updated_at: new Date().toISOString()
      };
      
      // Check if school_logo setting exists
      const { data: existingLogo, error: checkError } = await supabase
        .from('system_settings')
        .select('setting_id')
        .eq('setting_name', 'school_logo')
        .maybeSingle();
      
      if (checkError) throw checkError;
      
      if (existingLogo) {
        // Update existing setting
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({ 
            setting_value: logoSettings,
            updated_at: new Date()
          })
          .eq('setting_name', 'school_logo');
        
        if (updateError) throw updateError;
      } else {
        // Insert new setting
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{ 
            setting_name: 'school_logo',
            setting_value: logoSettings
          }]);
        
        if (insertError) throw insertError;
      }
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        school_logo: logoSettings
      }));
      
      setSuccess('Logo uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setError(error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-red-700">
              Access denied. Only administrators can access system settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            School Logo
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Upload your school logo for use in reports and exports
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="space-y-6">
            <div className="flex flex-col items-center">
              {logoPreview ? (
                <div className="mb-4 border border-gray-200 rounded-lg p-2 w-64 h-64 flex items-center justify-center">
                  <img 
                    src={logoPreview} 
                    alt="School Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="mb-4 border border-gray-200 rounded-lg p-2 w-64 h-64 flex items-center justify-center bg-gray-50">
                  <Image className="h-16 w-16 text-gray-400" />
                </div>
              )}
              
              <div className="flex flex-col items-center space-y-4">
                <label className="block">
                  <span className="sr-only">Choose logo file</span>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />
                </label>
                
                <button
                  onClick={handleLogoUpload}
                  disabled={!logoFile || uploadingLogo}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex items-start justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-500" />
              Sports Years
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Manage competition years. The active year is used as the default for new competitions,
              registrations, results and champions. Activating a year also snapshots each student's
              current house and department into that year's enrollment record. Use Snapshot to re-run
              this for an existing year (e.g. after adding more students).
            </p>
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6 space-y-6">
          {yearsLoading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sportsYears.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-4 text-center text-sm text-gray-500">
                        No sports years yet. Create one below.
                      </td>
                    </tr>
                  )}
                  {sportsYears.map((y) => (
                    <tr key={y.year_id} className={y.status === 'active' ? 'bg-green-50' : ''}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{y.year}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{y.label || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{y.start_date || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{y.end_date || '-'}</td>
                      <td className="px-4 py-2">{statusBadge(y.status)}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleSnapshotEnrollments(y)}
                            disabled={yearActionLoading === `snapshot-${y.year_id}`}
                            className="inline-flex items-center px-2 py-1 border border-blue-200 text-xs font-medium rounded text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                            title="Copy each student's current house and department into this year's enrollment record"
                          >
                            <Users className="h-3 w-3 mr-1" />
                            Snapshot
                          </button>
                          {y.status !== 'active' && (
                            <button
                              onClick={() => handleActivateYear(y.year)}
                              disabled={yearActionLoading === `activate-${y.year}`}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300"
                              title="Set as active year"
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Activate
                            </button>
                          )}
                          {y.status !== 'archived' && (
                            <button
                              onClick={() => handleArchiveYear(y)}
                              disabled={yearActionLoading === `archive-${y.year_id}` || y.status === 'active'}
                              className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                              title={y.status === 'active' ? 'Activate another year first' : 'Archive this year'}
                            >
                              <Archive className="h-3 w-3 mr-1" />
                              Archive
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteYear(y)}
                            disabled={yearActionLoading === `delete-${y.year_id}` || y.status === 'active'}
                            className="inline-flex items-center px-2 py-1 border border-red-200 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                            title={y.status === 'active' ? 'Cannot delete the active year' : 'Delete this year'}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form onSubmit={handleCreateSportsYear} className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <Plus className="h-4 w-4 mr-1" />
              Add new sports year
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700">Year *</label>
                <input
                  type="number"
                  required
                  min={1900}
                  max={2100}
                  value={newYear.year}
                  onChange={(e) => setNewYear((prev) => ({ ...prev, year: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Label</label>
                <input
                  type="text"
                  value={newYear.label}
                  onChange={(e) => setNewYear((prev) => ({ ...prev, label: e.target.value }))}
                  placeholder="e.g. 2025/2026"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Start date</label>
                <input
                  type="date"
                  value={newYear.start_date}
                  onChange={(e) => setNewYear((prev) => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">End date</label>
                <input
                  type="date"
                  value={newYear.end_date}
                  onChange={(e) => setNewYear((prev) => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Status</label>
                <select
                  value={newYear.status}
                  onChange={(e) => setNewYear((prev) => ({ ...prev, status: e.target.value as SportsYearStatus }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="planning">planning</option>
                  <option value="active">active</option>
                  <option value="archived">archived</option>
                </select>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="submit"
                disabled={yearActionLoading === 'create'}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              >
                <Plus className="h-4 w-4 mr-1" />
                {yearActionLoading === 'create' ? 'Creating...' : 'Add year'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Reference Number Settings
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Configure how reference numbers are generated for participants
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="auto_reference_number_enabled"
                  name="auto_reference_number_enabled"
                  type="checkbox"
                  checked={settings.auto_reference_number.enabled}
                  onChange={(e) => handleChange('auto_reference_number', 'enabled', e.target.checked)}
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="auto_reference_number_enabled" className="font-medium text-gray-700">
                  Enable automatic reference number generation
                </label>
                <p className="text-gray-500">
                  When enabled, new participants will automatically be assigned a sequential reference number
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="auto_reference_number_prefix" className="block text-sm font-medium text-gray-700">
                Reference Number Prefix (Optional)
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="auto_reference_number_prefix"
                  id="auto_reference_number_prefix"
                  value={settings.auto_reference_number.prefix}
                  onChange={(e) => handleChange('auto_reference_number', 'prefix', e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., P-"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Optional prefix to add before the number (e.g., "P-1000")
              </p>
            </div>

            <div>
              <label htmlFor="auto_reference_number_next" className="block text-sm font-medium text-gray-700">
                Next Reference Number
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="auto_reference_number_next"
                  id="auto_reference_number_next"
                  min="1"
                  value={settings.auto_reference_number.next_number}
                  onChange={(e) => handleChange('auto_reference_number', 'next_number', parseInt(e.target.value))}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                The next number that will be assigned to a new participant
              </p>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleGenerateReferenceNumbers}
                disabled={generating}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshCw className={`-ml-1 mr-2 h-5 w-5 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Generating...' : 'Generate Reference Numbers for Existing Participants'}
              </button>

              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsPage;